import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';
import { title } from '@core/utils/filter';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default function useItemGroupList() {
  // Use toast
  const toast = useToast();

  const refItemGroupListTable = ref(null);

  // Loading state
  const isTableLoading = ref(false);

  // Parent groups lookup map
  const parentGroupsMap = ref({});

  // Fetch all item groups for parent lookup
  const fetchParentGroups = async () => {
    try {
      const allGroups = await store.dispatch('fbr-invoice-item-group/fetchAllItemGroups');
      const lookupMap = {};
      allGroups.forEach((group) => {
        lookupMap[group.id] = group.name;
      });
      parentGroupsMap.value = lookupMap;
    } catch (error) {
      console.error('Error fetching parent groups:', error);
    }
  };

  // Table Handlers
  const tableColumns = [
    { key: 'serial', label: 'SNo', sortable: false, thStyle: { minWidth: '60px' } },
    { key: 'id', label: 'ID', sortable: false, thStyle: { minWidth: '60px' } },
    { key: 'code', sortable: false, thStyle: { minWidth: '120px' }, stickyColumn: true },
    { key: 'parentID', sortable: false, label: 'Parent', thStyle: { minWidth: '120px' } },
    { key: 'name', sortable: false, thStyle: { minWidth: '200px' } },
    { key: 'groupType', sortable: false, thStyle: { minWidth: '120px' } },
    { key: 'itemType', sortable: false, thStyle: { minWidth: '150px' } },
    { key: 'createdBy', sortable: false, thStyle: { minWidth: '120px' } },
    { key: 'updatedBy', sortable: false, thStyle: { minWidth: '120px' } },
    { key: 'actions', sortable: false, stickyColumn: true, thStyle: { minWidth: '120px' } },
  ];
  const perPage = ref(10);
  const totalItemGroups = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');

  // Filter states
  const groupTypeFilter = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refItemGroupListTable.value
      ? refItemGroupListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalItemGroups.value,
    };
  });

  const refetchData = () => {
    // Force the table to refresh by triggering a new API call
    if (refItemGroupListTable.value) {
      // Reset to first page when filters change
      currentPage.value = 1;
      // Trigger the table refresh which will call fetchItemGroups with new filter values
      refItemGroupListTable.value.refresh();
    }
  };

  // Debounced search function
  const debouncedSearch = debounce(() => {
    refetchData();
  }, 500); // 500ms delay

  // Watch for page and perPage changes (immediate)
  watch([currentPage, perPage], () => {
    refetchData();
  });

  // Watch for search query changes (debounced)
  watch(searchQuery, () => {
    debouncedSearch();
  });

  // Watch for filter changes
  watch(groupTypeFilter, (newValue, oldValue) => {
    console.log('=== useItemGroupList groupTypeFilter changed:', { oldValue, newValue });
    refetchData();
  });

  const fetchItemGroups = (ctx, callback) => {
    console.log('=== useItemGroupList fetchItemGroups()');

    isTableLoading.value = true;

    const params = {
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      groupType: groupTypeFilter.value,
    };

    console.log('=== useItemGroupList fetchItemGroups() params:', params);

    store
      .dispatch('fbr-invoice-item-group/fetchItemGroups', params)
      .then((response) => {
        console.log('=== useItemGroupList fetchItemGroups() response', response);

        const { items, pagination } = response;
        console.log('=== useItemGroupList items:', items);
        console.log('=== useItemGroupList pagination:', pagination);
        callback(items);
        totalItemGroups.value = pagination.totalCount;
      })
      .catch((error) => {
        console.log('=== useItemGroupList fetchItemGroups() error', error);

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching Item Groups list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      })
      .finally(() => {
        isTableLoading.value = false;
      });
  };

  return {
    fetchItemGroups,
    tableColumns,
    perPage,
    currentPage,
    totalItemGroups,
    dataMeta,
    perPageOptions,
    searchQuery,
    refItemGroupListTable,

    // Loading state
    isTableLoading,

    // Parent groups
    parentGroupsMap,
    fetchParentGroups,

    refetchData,

    // Filter states
    groupTypeFilter,
  };
}
